<template>
  <section id="ai-hero-wrapper" ref="aiHeroWrapper" class="ai-hero">
    <section class="ai-hero__content container">
      <div id="ai-hero-text-content" class="ai-hero__text-content">
        <div
          id="ai-hero-animation-container"
          class="ai-hero__left-animation-container"
        >
          <div id="ai-hero-left-intro">
            <p class="ai-hero__left-subheading">
              {{ $t('common.trusted_ai.subheading') }}
            </p>
            <h2 class="ai-hero__left-heading">
              {{ $t('common.trusted_ai.heading') }}
            </h2>
            <p id="ai-hero-description" class="ai-hero__left-description">
              {{ $t('common.trusted_ai.description') }}
            </p>
          </div>
          <div
            id="ai-hero-left-elements-wrapper"
            class="ai-hero__left-elements--wrapper"
          >
            <div id="ai-hero-left-elements" class="ai-hero__left-elements">
              <div>
                <h4 class="ai-hero__element-heading">
                  {{ $t('common.trusted_ai.element_heading_1') }}
                </h4>
                <p class="ai-hero__left-paragraph">
                  {{ $t('common.trusted_ai.left_paragraph_1') }}
                </p>
              </div>
              <div>
                <h4 class="ai-hero__element-heading">
                  {{ $t('common.trusted_ai.element_heading_2') }}
                </h4>
                <p class="ai-hero__left-paragraph">
                  {{ $t('common.trusted_ai.left_paragraph_2') }}
                </p>
              </div>
              <div>
                <h4 class="ai-hero__element-heading">
                  {{ $t('common.trusted_ai.element_heading_3') }}
                </h4>
                <p class="ai-hero__left-paragraph">
                  {{ $t('common.trusted_ai.left_paragraph_3') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BaseNuxtImage
        id="ai-hero-star-background"
        src="https://a.storyblok.com/f/120667/1307x895/7d48e18da2/star_background.png"
        class="ai-hero__star-background"
        alt=""
        height="auto"
      />
      <div id="ai-hero-cta-section" class="ai-hero__cta-section">
        <p id="ai-hero-cta-subheading" class="ai-hero__cta-subheading">
          {{ $t('common.trusted_ai.cta_subheading') }}
        </p>
        <h1
          class="ai-hero__cta-heading"
          v-html="$t('common.trusted_ai.cta_heading')"
        />
        <BaseButton
          id="ai-hero-cta-button"
          :title="$t('common.trusted_ai.cta_button')"
          class="ai-hero__cta-button"
          @click="onCtaClick"
        >
          <template #text>
            {{ $t('common.trusted_ai.cta_button') }}
          </template>
        </BaseButton>
      </div>
      <div id="ai-hero-graphic-wrapper" class="ai-hero__graphic-wrapper">
        <BaseNuxtImage
          id="ai-hero-inter-layer-1"
          class="ai-hero__inter-layer-1"
          src="https://a.storyblok.com/f/120667/756x275/42dc52999b/inter_layer_1.png"
          alt="Infermedica AI"
          height="auto"
        />
        <BaseNuxtImage
          id="ai-hero-layer-1"
          class="ai-hero__layer-1"
          src="https://a.storyblok.com/f/120667/756x520/8200dee7a1/layer_1.png"
          alt="Infermedica AI"
          height="auto"
        />
        <BaseNuxtImage
          id="ai-hero-inter-layer-2"
          class="ai-hero__inter-layer-2"
          src="https://a.storyblok.com/f/120667/552x252/e8591c9268/inter_layer_2.png"
          alt="Infermedica AI"
          height="auto"
        />
        <BaseNuxtImage
          id="ai-hero-layer-2"
          class="ai-hero__layer-2"
          src="https://a.storyblok.com/f/120667/756x450/ad80a7b7b1/layer_2.png"
          alt="Infermedica AI"
          height="auto"
        />
        <BaseNuxtImage
          id="ai-hero-inter-layer-3"
          class="ai-hero__inter-layer-3"
          src="https://a.storyblok.com/f/120667/738x348/a8fc85a8e1/inter_layer_3.png"
          alt="Infermedica AI"
          height="auto"
        />
        <BaseNuxtImage
          id="ai-hero-layer-3"
          class="ai-hero__layer-3"
          src="https://a.storyblok.com/f/120667/756x442/4e0bc9ef32/layer_3.png"
          alt="Infermedica AI"
          height="auto"
        />
        <div id="ai-hero-glow" class="ai-hero__glow" />
      </div>
    </section>
  </section>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { Flip } from 'gsap/dist/Flip'
import { Observer } from 'gsap/dist/Observer'

gsap.registerPlugin(ScrollTrigger)
gsap.registerPlugin(Flip)
gsap.registerPlugin(Observer)

const getElementHeight = (element: HTMLElement) => {
  return element.getBoundingClientRect().height
}

const onCtaClick = () => {
  window.scrollTo({
    top: (3000 / 5) * 2,
    behavior: 'smooth',
  })
}

onMounted(() => {
  const leftIntroElementHeight = getElementHeight(
    document.querySelector('#ai-hero-left-intro') as HTMLElement,
  )
  const firstElementHeight = getElementHeight(
    document.querySelector(
      '.ai-hero__left-elements > div:first-child',
    ) as HTMLElement,
  )
  const secondElementHeight = getElementHeight(
    document.querySelector(
      '.ai-hero__left-elements > div:nth-child(2)',
    ) as HTMLElement,
  )

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: '#ai-hero-wrapper',
      pin: true,
      start: 'top +=88px',
      end: '+=3000',
      scrub: true,
    },
    defaults: {
      ease: 'none',
    },
  })

  const stageOne = (tl: gsap.core.Timeline) => {
    return tl
      .fromTo(
        '#ai-hero-cta-section',
        {
          scale: 1,
          y: 0,
        },
        {
          scale: 0.699,
          y: -220,
        },
      )
      .fromTo(
        '#ai-hero-cta-button',
        {
          scale: 1,
        },
        {
          scale: 1.3,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-cta-subheading',
        {
          opacity: 1,
          y: 0,
        },
        {
          opacity: 0,
          y: -50,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-graphic-wrapper',
        {
          y: 320,
          opacity: 0.5,
        },
        {
          y: 50,
          opacity: 1,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-layer-1',
        {
          y: 0,
        },
        {
          y: 0,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-layer-2',
        {
          y: 0,
          opacity: 0,
        },
        {
          y: 25,
          opacity: 1,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-layer-3',
        {
          y: 0,
          opacity: 0,
        },
        {
          y: 50,
          opacity: 1,
        },
        '<',
      )
  }

  const stageTwo = (tl: gsap.core.Timeline) => {
    tl.fromTo(
      '#ai-hero-cta-section',
      {
        y: -220,
        opacity: 1,
      },
      {
        y: -520,
        opacity: 0,
      },
    )
      .fromTo(
        '#ai-hero-graphic-wrapper',
        {
          width: 567,
          top: '50%',
          right: '50%',
          x: '50%',
          y: 50,
        },
        {
          width: 378,
          top: 200,
          right: 0,
          x: 0,
          y: 0,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-text-content',
        {
          opacity: 0,
          y: '100%',
        },
        {
          opacity: 1,
          y: 0,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-glow',
        {
          opacity: 0.3,
        },
        {
          opacity: 0,
        },
        '<',
      )
  }

  const stageThree = (tl: gsap.core.Timeline) => {
    tl.fromTo(
      '#ai-hero-description',
      {
        opacity: 1,
      },
      {
        opacity: 0,
      },
    )
      .fromTo(
        '#ai-hero-layer-3',
        {
          y: 50,
        },
        {
          y: 200,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-inter-layer-3',
        {
          scale: 0.8,
          y: 0,
          opacity: 0,
        },
        {
          y: 175,
          opacity: 1,
          scale: 1,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-left-elements-wrapper',
        {
          y: 0,
        },
        {
          y: -leftIntroElementHeight - 100,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-left-intro',
        {
          opacity: 1,
          y: 0,
        },
        {
          y: -leftIntroElementHeight - 100,
          opacity: 0,
        },
        '<',
      )
  }

  const stageFour = (tl: gsap.core.Timeline) => {
    tl.fromTo(
      '#ai-hero-inter-layer-3',
      {
        scale: 1,
        opacity: 1,
      },
      {
        scale: 0.8,
        opacity: 0,
      },
    )
      .fromTo(
        '#ai-hero-layer-2',
        {
          y: 25,
        },
        {
          y: 175,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-inter-layer-2',
        {
          scale: 0.8,
          opacity: 0,
          y: 0,
        },
        {
          y: 190,
          scale: 1,
          opacity: 1,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-left-elements',
        {
          y: 0,
        },
        {
          y: -firstElementHeight - 64,
        },
        '<',
      )
  }

  const stageFive = (tl: gsap.core.Timeline) => {
    tl.fromTo(
      '#ai-hero-inter-layer-2',
      {
        scale: 1,
        opacity: 1,
      },
      {
        scale: 0.8,
        opacity: 0,
      },
    )
      .fromTo(
        '#ai-hero-layer-1',
        {
          y: 0,
        },
        {
          y: 150,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-inter-layer-1',
        {
          scale: 0.8,
          opacity: 0,
          y: 0,
        },
        {
          y: 175,
          scale: 1,
          opacity: 1,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-left-elements',
        {
          y: -firstElementHeight - 64,
        },
        {
          y: -firstElementHeight - secondElementHeight - 128,
        },
        '<',
      )
      .fromTo(
        '#ai-hero-graphic-wrapper',
        {
          y: 0,
        },
        {
          y: -155,
        },
        '<',
      )
  }

  const stages = [stageOne, stageTwo, stageThree, stageFour, stageFive]

  stages.forEach((stage) => {
    stage(tl)
  })

  window.scrollTo({
    top: 0,
    behavior: 'instant',
  })
})
</script>

<style lang="scss" scoped>
.ai-hero {
  color: white;
  height: calc(100vh - var(--header-height));
  background-color: var(--cobalt-800);
  position: relative;

  &__content {
    position: relative;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    z-index: 0;
  }

  &__cta-section {
    z-index: 4;
    max-width: 990px;
    margin: 0 auto;
    text-align: center;
  }

  &__cta-subheading {
    font-size: var(--font-size-paragraph-m);
    font-weight: 600;
    margin-bottom: 34px;
  }

  &__cta-heading {
    color: white;
    font-size: 75px;
    line-height: 95px;
    margin-bottom: 46px;
  }

  &__star-background {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    object-fit: cover;
    z-index: -1;
    width: 100vw;
    height: 100%;
  }

  $graphic-layer-width: 567px;

  &__graphic-wrapper {
    opacity: 0.2;
    position: absolute;
    top: 50%;
    right: 50%;
    transform: translateX(50%);
    pointer-events: none;
    width: $graphic-layer-width;
    height: 207px;
  }

  &__layer {
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
  }

  &__layer-1 {
    @extend .ai-hero__layer;

    z-index: 5;
  }

  &__layer-2 {
    @extend .ai-hero__layer;

    z-index: 3;
  }

  &__layer-3 {
    @extend .ai-hero__layer;

    z-index: 1;
  }

  &__inter-layer {
    position: absolute;
    bottom: 0;
    left: 50%;
    opacity: 0;
    transform: translateX(-50%);
  }

  &__inter-layer-1 {
    @extend .ai-hero__inter-layer;

    width: 100%;
    top: -10px;
    z-index: 6;
  }

  &__inter-layer-2 {
    @extend .ai-hero__inter-layer;

    z-index: 4;
    top: 20px;
    left: 54%;
    width: 73%;
  }

  &__inter-layer-3 {
    @extend .ai-hero__inter-layer;

    width: 100%;
    z-index: 2;
    top: 4px;
  }

  &__left-subheading {
    font-size: var(--font-size-paragraph-2xs);
    color: white;
    font-weight: 600;
    margin-bottom: 20px;
  }

  &__left-heading {
    color: white;
    margin-bottom: 20px;
  }

  &__element-heading {
    color: white;
    margin-bottom: 16px;
  }

  &__left-description {
    margin-bottom: 100px;

    &--absolute {
      position: absolute;
    }
  }

  &__left-paragraph {
    margin-bottom: 0;
  }

  &__left-elements {
    display: flex;
    flex-direction: column;
    row-gap: 64px;

    &--wrapper {
      overflow: hidden;
      position: relative;
    }
  }

  &__text-content {
    color: white;
    opacity: 0;
    position: absolute;
    top: 0;
    padding-top: 120px;
    max-width: 500px;
  }

  &__left-animation-container {
    position: relative;
  }

  &__glow {
    width: 680px;
    height: 530px;
    background-color: #2e85ff;
    opacity: 0.3;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 10000px;
    filter: blur(100px);
  }
}
</style>
